import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, CheckCircle2, ArrowRight } from "lucide-react";
import { PageHeader } from "../components/ui-blocks";
import { Reveal } from "../components/motion";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { BRAND } from "../data";

const INFO = [
  { icon: Phone, label: "Call us", phones: BRAND.phones },
  { icon: Mail, label: "Email us", value: BRAND.email, href: `mailto:${BRAND.email}` },
  { icon: MapPin, label: "Visit us", value: BRAND.address },
  { icon: Clock, label: "Hours", value: "Mon–Fri · 8:00 AM – 6:00 PM MT" },
];

export default function Contact() {
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", topic: "", message: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please fill in name, email and message."); return; }
    setSent(true);
    toast.success("Message sent — we'll be in touch shortly.");
  };

  return (
    <div data-testid="contact-page">
      <PageHeader overline="Contact" title="Let's start a conversation" subtitle="Whether you're hiring, job hunting, or just exploring — we'd love to hear from you. We reply to every message." />

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-4">
            {INFO.map((it, i) => (
              <Reveal key={it.label} delay={i * 0.05}>
                <div className="flex gap-4 rounded-2xl border border-slate-800/80 bg-[#111827]/60 p-6 hover:border-amber-500/30 transition-all">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400"><it.icon className="w-6 h-6" /></span>
                  <div>
                    <div className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-1">{it.label}</div>
                    {it.phones ? (
                      <div className="space-y-0.5">
                        {it.phones.map((p) => (
                          <a key={p} href={`tel:${p.replace(/[^+\d]/g, "")}`} className="block text-white hover:text-amber-400 transition-colors">{p}</a>
                        ))}
                      </div>
                    ) : it.href ? <a href={it.href} className="text-white hover:text-amber-400 transition-colors">{it.value}</a> : <div className="text-white">{it.value}</div>}
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.2}>
              <div className="rounded-2xl border border-slate-800/80 overflow-hidden h-52">
                <iframe title="Hirezen HQ" className="w-full h-full grayscale opacity-80" style={{ filter: "grayscale(1) invert(0.9) hue-rotate(180deg)" }} src="https://www.openstreetmap.org/export/embed.html?bbox=-111.9520%2C40.6040%2C-111.9260%2C40.6160&layer=mapnik&marker=40.6100%2C-111.9391" />
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal>
              <div className="relative rounded-3xl border border-slate-800/80 bg-[#111827]/80 p-8 sm:p-10 overflow-hidden">
                <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-amber-500/10 blur-[90px] pointer-events-none" />
                <AnimatePresence mode="wait">
                  {sent ? (
                    <motion.div key="s" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="relative py-12 text-center" data-testid="contact-success">
                      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/15 border border-amber-500/30 mb-5"><CheckCircle2 className="w-8 h-8 text-amber-400" /></div>
                      <h3 className="text-2xl font-bold text-white mb-2">Message sent</h3>
                      <p className="text-slate-400 max-w-md mx-auto">Thanks, {form.name.split(" ")[0] || "there"}. We've got your message and will reply to {form.email} shortly.</p>
                      <button onClick={() => { setSent(false); setForm({ name: "", email: "", phone: "", topic: "", message: "" }); }} className="mt-7 px-5 py-2.5 text-sm font-semibold text-slate-200 border border-slate-700 rounded-xl hover:border-slate-500 transition-all" data-testid="contact-reset">Send another</button>
                    </motion.div>
                  ) : (
                    <motion.form key="f" onSubmit={submit} className="relative space-y-5" data-testid="contact-form">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5"><Label className="text-xs text-slate-400">Name *</Label><Input data-testid="contact-name" value={form.name} onChange={set("name")} placeholder="Jane Doe" className="input-dark" /></div>
                        <div className="space-y-1.5"><Label className="text-xs text-slate-400">Email *</Label><Input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} placeholder="jane@email.com" className="input-dark" /></div>
                        <div className="space-y-1.5"><Label className="text-xs text-slate-400">Phone</Label><Input data-testid="contact-phone" value={form.phone} onChange={set("phone")} placeholder="+1 (555) …" className="input-dark" /></div>
                        <div className="space-y-1.5"><Label className="text-xs text-slate-400">I'm reaching out as</Label>
                          <Select value={form.topic} onValueChange={(v) => setForm((f) => ({ ...f, topic: v }))}>
                            <SelectTrigger data-testid="contact-topic" className="input-dark"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>{["An employer hiring", "A job seeker", "A partner", "Press / Media", "Other"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-1.5"><Label className="text-xs text-slate-400">Message *</Label><Textarea data-testid="contact-message" value={form.message} onChange={set("message")} rows={5} placeholder="How can we help?" className="input-dark resize-none" /></div>
                      <button type="submit" data-testid="contact-form-submit" className="group w-full inline-flex items-center justify-center gap-2 px-6 py-4 text-sm font-semibold text-slate-950 bg-gradient-to-r from-amber-400 to-amber-500 rounded-xl shadow-lg shadow-amber-500/25 hover:shadow-amber-500/45 transition-all active:scale-[0.99]">Send Message <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></button>
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
